/**
 * CommandPalette — global ⌘K palette.
 *
 * Three sections:
 *   1. Quick create — new task, new project, new client, etc.
 *      Only shows actions permitted by the current user's RBAC.
 *   2. Navigate — every nav item from AppShell NAV_GROUPS, permission-filtered.
 *   3. Search results — hits from /workspaces/{ws}/search (clients, projects,
 *      review items, comments).
 *
 * Selecting a Quick create item navigates to the target page with `?new=1`
 * query param — target pages already read that to auto-open their create
 * dialog. Selecting a Nav or Search item navigates to that page.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import useWorkspacePerms from "@/lib/useWorkspacePerms";
import { NAV_GROUPS } from "@/components/Layout/AppShell";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/components/ui/command";
import { DialogTitle } from "@/components/ui/dialog";
import {
  Plus, ListChecks, FolderKanban, Users, Briefcase, Bell, Gem, ClipboardList,
  MessageSquare, FileText, Megaphone, PieChart, ArrowRight, Star,
} from "lucide-react";
import { openFeedbackDialog } from "@/components/GlobalFeedbackLauncher";

// Quick-create actions. `perm` uses the same anyOf semantics as PermGate:
// if any of the listed perms is granted, the action is shown.
const QUICK_ACTIONS = [
  {
    key: "task",
    label: "New task",
    icon: ListChecks,
    keywords: "task todo tsk",
    to: "/app/tasks?new=1",
    perm: ["task.manage", "task.create", "project.manage"],
  },
  {
    key: "project",
    label: "New project",
    icon: FolderKanban,
    keywords: "proj project prj",
    to: "/app/projects?new=1",
    perm: ["project.create", "project.manage", "workspace.manage"],
  },
  {
    key: "client",
    label: "New client",
    icon: Users,
    keywords: "client cust",
    to: "/app/clients?new=1",
    perm: ["client.manage", "workspace.manage"],
  },
  {
    key: "brand",
    label: "New brand",
    icon: Gem,
    keywords: "brand bnd",
    to: "/app/brands?new=1",
    perm: ["brand.manage", "workspace.manage"],
  },
  {
    key: "lead",
    label: "New lead",
    icon: Briefcase,
    keywords: "lead prospect deal",
    to: "/app/leads?new=1",
    perm: ["lead.manage"],
  },
  {
    key: "reminder",
    label: "New reminder",
    icon: Bell,
    keywords: "rem reminder ping",
    to: "/app/reminders/team?new=1",
    perm: ["task.view", "task.manage"],
  },
  {
    key: "note",
    label: "New bulletin post",
    icon: Megaphone,
    keywords: "note post bulletin update announce",
    to: "/app/home?new=1",
    perm: ["bulletin.view", "workspace.manage"],
  },
  {
    key: "meeting",
    label: "New meeting",
    icon: ClipboardList,
    keywords: "meet mtg meeting",
    to: "/app/meetings?new=1",
    perm: ["meeting.manage", "meeting.create"],
  },
  {
    key: "invoice",
    label: "New invoice",
    icon: FileText,
    keywords: "inv invoice bill",
    to: "/app/sales?new_invoice=1",
    perm: ["invoice.manage"],
  },
  {
    key: "portfolio",
    label: "New portfolio",
    icon: PieChart,
    keywords: "portfolio port folio boardroom exec",
    to: "/app/portfolios?new=1",
    perm: ["portfolio.manage", "workspace.manage"],
  },
];

// Flatten NAV_GROUPS into a list of navigation targets.
function useNavTargets(can, ready, navOverrides) {
  return useMemo(() => {
    const out = [];
    NAV_GROUPS.forEach((group) => {
      group.items.forEach((item) => {
        const ov = navOverrides?.[item.testid];
        if (ov === "hide") return;
        if (ov !== "show" && item.perm && !(ready && item.perm.some((p) => can(p)))) return;
        out.push({
          key: item.testid || item.to,
          label: item.label,
          icon: item.icon,
          to: item.to,
          group: group.label,
        });
      });
    });
    return out;
  }, [can, ready, navOverrides]);
}

const RECENT_KEY = "acq_palette_recent_v1";

function useRecent(activeWorkspaceId) {
  const [recent, setRecent] = useState(() => {
    try {
      const raw = localStorage.getItem(`${RECENT_KEY}_${activeWorkspaceId}`);
      const parsed = raw ? JSON.parse(raw) : [];
      // R138be · Legacy entries persisted before the icon-strip fix may
      // still carry an `icon: {}` (Lucide forwardRef serialized to a bare
      // object by JSON.stringify). Rendering `<r.icon />` on such an
      // object throws React error #130. Sanitise on load.
      return Array.isArray(parsed)
        ? parsed.map(({ icon, ...rest }) => rest)  // drop any stale icon
        : [];
    } catch { return []; }
  });
  const bump = (item) => {
    setRecent((prev) => {
      // R138be · Never persist Lucide component references — they don't
      // survive JSON.stringify (become {}), then crash on rehydrate.
      const { icon: _icon, ...clean } = item || {};
      const next = [clean, ...prev.filter((r) => r.key !== clean.key)].slice(0, 6);
      try {
        localStorage.setItem(`${RECENT_KEY}_${activeWorkspaceId}`,
                              JSON.stringify(next));
      } catch { /* ignore */ }
      return next;
    });
  };
  return { recent, bump };
}

export default function CommandPalette({ open, onClose }) {
  const nav = useNavigate();
  const { activeWorkspaceId, user } = useAuth();
  const { can, ready, navOverrides } = useWorkspacePerms();
  const [q, setQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [members, setMembers] = useState([]);
  const [canGiveFeedback, setCanGiveFeedback] = useState(false);
  const navTargets = useNavTargets(can, ready, navOverrides);
  const { recent, bump } = useRecent(activeWorkspaceId);

  useEffect(() => {
    if (open) setQ("");
  }, [open]);

  // Load workspace teammates + feedback permission when opened.
  useEffect(() => {
    if (!open || !activeWorkspaceId) return;
    (async () => {
      try {
        const r = await api.get(`/workspaces/${activeWorkspaceId}/members`);
        setMembers(Array.isArray(r.data) ? r.data : []);
      } catch { setMembers([]); }
      try {
        const r2 = await api.get(`/workspaces/${activeWorkspaceId}/feedback/can-create-internal`);
        setCanGiveFeedback(!!r2.data?.allowed);
      } catch { setCanGiveFeedback(false); }
    })();
  }, [open, activeWorkspaceId]);

  // Live search hit
  useEffect(() => {
    if (!q || !activeWorkspaceId) { setSearchResults(null); return; }
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get(
          `/workspaces/${activeWorkspaceId}/search`, { params: { q } });
        setSearchResults(data);
      } catch { setSearchResults(null); }
    }, 220);
    return () => clearTimeout(handle);
  }, [q, activeWorkspaceId]);

  const go = (item) => {
    bump(item);
    onClose?.();
    nav(item.to);
  };

  const availableQuickActions = useMemo(() => {
    if (!ready) return [];
    return QUICK_ACTIONS.filter((a) => {
      if (!a.perm || a.perm.length === 0) return true;
      return a.perm.some((p) => can(p));
    });
  }, [ready, can]);

  return (
    <CommandDialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogTitle className="sr-only">Command palette</DialogTitle>
      <CommandInput
        placeholder="Type task, proj, client, note, /invoice — or search anything..."
        value={q}
        onValueChange={setQ}
        data-testid="cmd-palette-input"
      />
      <CommandList data-testid="cmd-palette-list">
        <CommandEmpty>No matching commands.</CommandEmpty>

        {recent.length > 0 && !q && (
          <>
            <CommandGroup heading="Recent">
              {recent.map((r) => (
                <CommandItem
                  key={`recent-${r.key}`}
                  value={`recent ${r.label} ${r.keywords || ""}`}
                  onSelect={() => go(r)}
                  data-testid={`cmd-recent-${r.key}`}
                >
                  {typeof r.icon === "function" || (r.icon && r.icon.$$typeof)
                    ? <r.icon className="h-4 w-4 opacity-70" />
                    : <ArrowRight className="h-4 w-4 opacity-70" />}
                  <span>{r.label}</span>
                  {r.group && <span className="ml-auto text-[10px] text-muted-foreground uppercase tracking-wider">{r.group}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {availableQuickActions.length > 0 && (
          <CommandGroup heading="Create new">
            {availableQuickActions.map((a) => (
              <CommandItem
                key={`create-${a.key}`}
                value={`create ${a.label} ${a.keywords}`}
                onSelect={() => go({ ...a, icon: a.icon })}
                data-testid={`cmd-create-${a.key}`}
              >
                <Plus className="h-4 w-4 opacity-70" />
                <span>{a.label}</span>
                <span className="ml-auto text-[10px] text-muted-foreground uppercase tracking-wider">Create</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {canGiveFeedback && members.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Give feedback to teammate">
              {members
                .filter((m) => (m.user_id || m.user?.id) !== user?.id)
                .slice(0, 20)
                .map((m) => {
                  const uid = m.user_id || m.user?.id;
                  const name = m.name || m.user?.name || m.email || m.user?.email || "Teammate";
                  return (
                    <CommandItem
                      key={`fb-${uid}`}
                      value={`feedback give rate review ${name}`}
                      onSelect={() => {
                        onClose?.();
                        openFeedbackDialog(uid, name);
                      }}
                      data-testid={`cmd-feedback-${uid}`}
                    >
                      <Star className="h-4 w-4 opacity-70" />
                      <span>Give feedback to {name}</span>
                      <span className="ml-auto text-[10px] text-muted-foreground uppercase tracking-wider">Feedback</span>
                    </CommandItem>
                  );
                })}
            </CommandGroup>
          </>
        )}

        {navTargets.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Navigate">
              {navTargets.map((n) => (
                <CommandItem
                  key={`nav-${n.key}`}
                  value={`nav ${n.label} ${n.group}`}
                  onSelect={() => go({ ...n, icon: n.icon })}
                  data-testid={`cmd-nav-${n.key}`}
                >
                  {n.icon ? <n.icon className="h-4 w-4 opacity-70" /> : <ArrowRight className="h-4 w-4 opacity-70" />}
                  <span>{n.label}</span>
                  <span className="ml-auto text-[10px] text-muted-foreground uppercase tracking-wider">{n.group}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {q && searchResults && (
          <>
            <CommandSeparator />
            <SearchSection title="Clients" icon={Users}
                            items={searchResults.clients} onPick={(c) => go({ key: `client-${c.id}`, label: c.name, to: `/app/clients/${c.id}`, icon: Users, group: "Search" })} />
            <SearchSection title="Projects" icon={FolderKanban}
                            items={searchResults.projects} onPick={(p) => go({ key: `project-${p.id}`, label: p.name, to: `/app/projects/${p.id}`, icon: FolderKanban, group: "Search" })} />
            <SearchSection title="Review items" icon={FileText}
                            items={searchResults.review_items}
                            labelFn={(i) => `${i.title} · ${i.item_type} v${i.current_version_number}`}
                            onPick={(i) => go({ key: `item-${i.id}`, label: i.title, to: `/app/review-items/${i.id}`, icon: FileText, group: "Search" })} />
            <SearchSection title="Comments" icon={MessageSquare}
                            items={searchResults.comments}
                            labelFn={(c) => (c.body || "").slice(0, 80)}
                            onPick={(c) => go({ key: `comment-${c.id}`, label: (c.body || "").slice(0, 40), to: `/app/review-items/${c.review_item_id}`, icon: MessageSquare, group: "Search" })} />
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}

function SearchSection({ title, icon: Icon, items, onPick, labelFn }) {
  if (!items || items.length === 0) return null;
  return (
    <CommandGroup heading={title}>
      {items.map((it) => (
        <CommandItem
          key={`search-${title}-${it.id}`}
          value={`search ${title} ${labelFn ? labelFn(it) : it.name || it.title || it.body}`}
          onSelect={() => onPick(it)}
          data-testid={`cmd-search-${title.toLowerCase()}-${it.id}`}
        >
          <Icon className="h-4 w-4 opacity-70" />
          <span className="truncate">{labelFn ? labelFn(it) : (it.name || it.title || it.body)}</span>
          <span className="ml-auto text-[10px] text-muted-foreground uppercase tracking-wider">Search</span>
        </CommandItem>
      ))}
    </CommandGroup>
  );
}

// End of file
